import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { settingsService } from '../services/settingsService';

const UserPreferencesContext = createContext({
  preferences: {
    date_format: 'DD/MM/YYYY',
    time_format: '12h',
    first_day_of_week: 'monday',
    number_format: 'en-AU'
  },
  updatePreferences: () => {},
  loadPreferences: () => {}
});

export const UserPreferencesProvider = ({ children }) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    date_format: 'DD/MM/YYYY',
    time_format: '12h',
    first_day_of_week: 'monday',
    number_format: 'en-AU'
  });
  const [loading, setLoading] = useState(false);

  const loadPreferences = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data } = await settingsService.getUserProfile();
      if (data) {
        setPreferences({
          date_format: data.date_format || 'DD/MM/YYYY',
          time_format: data.time_format || '12h',
          first_day_of_week: data.first_day_of_week || 'monday',
          number_format: data.number_format || 'en-AU'
        });
      }
    } catch (e) {
      console.warn('Could not load user preferences:', e);
      // Keep defaults on error
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user, loadPreferences]);

  // Update local state (called after saving preferences in settings)
  const updatePreferences = useCallback((newPrefs) => {
    setPreferences(prev => ({ ...prev, ...newPrefs }));
  }, []);

  const value = {
    preferences,
    updatePreferences,
    loadPreferences,
    loading
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};

export default UserPreferencesContext;
